import { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../context/AuthContext';
import { useOrg } from '../context/OrgContext';
import {
  subscribeToCropTypes,
  createCropType,
  updateCropType,
  deleteCropType,
  type CropType,
} from '../services/cropType.service';
import { getAllSeasonTimes } from '../services/seasonTime.service';
import { getUsedCropTypeIds } from '../services/customerCrop.service';
import { toastSuccess, toastError } from '../components/core';
import { Navbar } from '../components/Navbar';
import {
  BackButton,
  Button,
  Badge,
  Card,
  ConfirmDialog,
  EmptyState,
  FAB,
  Spinner,
} from '../components/core';
import { Icon, PlusIcon, PencilIcon, TrashIcon, CloseIcon } from '../components/core';
import { isDuplicate } from '../utils/isDuplicate';

// ─── Helper ───────────────────────────────────────────────────────────────────

async function loadUsedIds(orgId: string): Promise<Set<string>> {
  const [customerIds, seasonTimes] = await Promise.all([
    getUsedCropTypeIds(orgId),
    getAllSeasonTimes(orgId),
  ]);
  const stIds = new Set(seasonTimes.map(st => st.cropTypeId));
  return new Set([...customerIds, ...stIds]);
}

// ─── Page ─────────────────────────────────────────────────────────────────────

export default function CropTypesPage() {
  const { user, signOut } = useAuth();
  const { org, orgId, orgSlug } = useOrg();
  const navigate = useNavigate();
  const { t } = useTranslation();

  // List state
  const [cropTypes, setCropTypes] = useState<CropType[]>([]);
  const [loading, setLoading] = useState(true);
  const [listError, setListError] = useState<string | null>(null);

  // Usage guard — which cropTypeIds are referenced by customer crops or season times
  const [usedIds, setUsedIds] = useState<Set<string>>(new Set());

  // Add form
  const [showAdd, setShowAdd] = useState(false);
  const [addName, setAddName] = useState('');
  const [addError, setAddError] = useState<string | null>(null);
  const [addSaving, setAddSaving] = useState(false);
  const addInputRef = useRef<HTMLInputElement>(null);

  // Inline edit
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editName, setEditName] = useState('');
  const [editError, setEditError] = useState<string | null>(null);
  const [editSaving, setEditSaving] = useState(false);
  const editInputRef = useRef<HTMLInputElement>(null);

  // Delete confirm
  const [deleteTarget, setDeleteTarget] = useState<{ id: string; name: string } | null>(null);
  const [deleting, setDeleting] = useState(false);

  // ─── Subscribe to crop types ───────────────────────────────────────────────
  useEffect(() => {
    const unsub = subscribeToCropTypes(
      orgId,
      data => {
        setCropTypes([...data].sort((a, b) => a.name.localeCompare(b.name)));
        setLoading(false);
      },
      () => {
        setListError(t('cropTypes.loadError'));
        setLoading(false);
      },
    );
    return unsub;
  }, [orgId, t]);

  // ─── Load usage data ───────────────────────────────────────────────────────
  useEffect(() => {
    loadUsedIds(orgId)
      .then(ids => setUsedIds(ids))
      .catch((err) => {
        console.error('[CropTypesPage] load used ids error:', err);
        // Non-critical — leave usedIds empty; delete buttons will all be enabled
      });
  }, [orgId]);

  // Focus add input when form opens
  useEffect(() => {
    if (showAdd) addInputRef.current?.focus();
  }, [showAdd]);

  // Focus edit input when edit starts
  useEffect(() => {
    if (editingId) editInputRef.current?.focus();
  }, [editingId]);

  // ─── Add handlers ──────────────────────────────────────────────────────────
  function handleAddOpen() {
    setShowAdd(true);
    setAddName('');
    setAddError(null);
  }

  function handleAddCancel() {
    setShowAdd(false);
    setAddName('');
    setAddError(null);
  }

  function handleAddBlur() {
    const trimmed = addName.trim();
    if (trimmed && isDuplicate(trimmed, cropTypes)) {
      setAddError(t('cropTypes.nameExists'));
    }
  }

  async function handleAddSave() {
    const trimmed = addName.trim();
    if (!trimmed) {
      setAddError(t('cropTypes.nameRequired'));
      return;
    }
    if (isDuplicate(trimmed, cropTypes)) {
      setAddError(t('cropTypes.nameExists'));
      return;
    }
    setAddSaving(true);
    setAddError(null);
    try {
      await createCropType(orgId, { name: trimmed });
      toastSuccess(t('cropTypes.toastCreated'));
      setShowAdd(false);
      setAddName('');
    } catch (err: unknown) {
      console.error('[CropTypesPage] add save error:', err);
      const code = err && typeof err === 'object' && 'code' in err ? (err as { code: string }).code : '';
      setAddError(code === 'already-exists' ? t('cropTypes.nameExists') : t('cropTypes.saveError'));
    } finally {
      setAddSaving(false);
    }
  }

  // ─── Edit handlers ─────────────────────────────────────────────────────────
  function handleEditStart(ct: CropType) {
    setEditingId(ct.id);
    setEditName(ct.name);
    setEditError(null);
  }

  function handleEditCancel() {
    setEditingId(null);
    setEditName('');
    setEditError(null);
  }

  function handleEditBlur() {
    const trimmed = editName.trim();
    if (trimmed && editingId && isDuplicate(trimmed, cropTypes, editingId)) {
      setEditError(t('cropTypes.nameExists'));
    }
  }

  async function handleEditSave() {
    const trimmed = editName.trim();
    if (!trimmed) {
      setEditError(t('cropTypes.nameRequired'));
      return;
    }
    if (!editingId) return;
    if (isDuplicate(trimmed, cropTypes, editingId)) {
      setEditError(t('cropTypes.nameExists'));
      return;
    }
    setEditSaving(true);
    setEditError(null);
    try {
      await updateCropType(orgId, editingId, trimmed);
      toastSuccess(t('cropTypes.toastUpdated'));
      setEditingId(null);
    } catch (err: unknown) {
      console.error('[CropTypesPage] edit save error:', err);
      const code = err && typeof err === 'object' && 'code' in err ? (err as { code: string }).code : '';
      setEditError(code === 'already-exists' ? t('cropTypes.nameExists') : t('cropTypes.saveError'));
    } finally {
      setEditSaving(false);
    }
  }

  // ─── Delete handlers ───────────────────────────────────────────────────────
  function handleDeleteClick(ct: CropType) {
    setDeleteTarget({ id: ct.id, name: ct.name });
  }

  async function handleDeleteConfirm() {
    if (!deleteTarget) return;
    setDeleting(true);
    try {
      await deleteCropType(orgId, deleteTarget.id);
      const refreshed = await loadUsedIds(orgId);
      setUsedIds(refreshed);
      toastSuccess(t('cropTypes.toastDeleted'));
      setDeleteTarget(null);
    } catch (err){
      console.error('[CropTypes] save error:', err);
      toastError(t('cropTypes.deleteError'));
      setDeleteTarget(null);
    } finally {
      setDeleting(false);
    }
  }

  // ─── Render ────────────────────────────────────────────────────────────────
  return (
    <div className="min-h-screen bg-green-50">
      <Navbar displayName={user?.displayName ?? null} onSignOut={signOut} onLogoClick={() => navigate('/')} onOrgNameClick={() => navigate(`/org/${orgSlug}`)} onAvatarClick={() => navigate('/profile')} orgName={org.name} />

      <main className="mx-auto max-w-2xl px-4 py-6 pb-24 sm:px-6 sm:pb-6">
        <BackButton />

        {/* Page heading */}
        <div className="mb-5 flex items-start justify-between gap-4">
          <div>
            <h1 className="flex items-center gap-2 font-display text-xl font-semibold text-gray-900">
              {t('cropTypes.title')}
              {!loading && <Badge>{cropTypes.length}</Badge>}
            </h1>
            <p className="mt-1 font-body text-sm text-gray-500">{t('cropTypes.subtitle')}</p>
          </div>
          <Button
            variant="primary"
            size="sm"
            icon={<Icon size="sm"><PlusIcon /></Icon>}
            onClick={handleAddOpen}
            className="hidden shrink-0 sm:inline-flex"
          >
            {t('cropTypes.add')}
          </Button>
        </div>

        {/* Content */}
        {loading ? (
          <div className="flex justify-center py-16">
            <Spinner size="lg" />
          </div>
        ) : listError ? (
          <div className="rounded bg-red-50 px-4 py-3 font-body text-sm text-red-600">
            {listError}
          </div>
        ) : (
          <Card variant="info">
            {/* Add form row */}
            {showAdd && (
              <div className="flex items-start gap-2 border-b border-green-100 px-4 py-3">
                <div className="flex-1">
                  <input
                    ref={addInputRef}
                    type="text"
                    value={addName}
                    onChange={e => {
                      setAddName(e.target.value);
                      if (addError) setAddError(null);
                    }}
                    onBlur={handleAddBlur}
                    onKeyDown={e => {
                      if (e.key === 'Enter') handleAddSave();
                      if (e.key === 'Escape') handleAddCancel();
                    }}
                    placeholder={t('cropTypes.namePlaceholder')}
                    maxLength={80}
                    disabled={addSaving}
                    className="w-full rounded-sm border border-gray-300 px-3 py-1.5 font-body text-sm text-gray-900 outline-none transition-agrobook duration-agrobook focus:border-green-600 focus:ring-4 focus:ring-green-600/15 disabled:bg-gray-50 disabled:text-gray-400"
                  />
                  {addError && (
                    <p className="mt-1 font-body text-xs text-red-600">{addError}</p>
                  )}
                </div>
                <Button variant="primary" size="sm" onClick={handleAddSave} disabled={addSaving}>
                  {addSaving ? <Spinner size="sm" /> : t('cropTypes.save')}
                </Button>
                <button
                  type="button"
                  onClick={handleAddCancel}
                  disabled={addSaving}
                  aria-label={t('cropTypes.cancelAdd')}
                  className="flex h-8 w-8 items-center justify-center rounded-sm text-gray-400 transition-agrobook duration-agrobook hover:text-gray-700 disabled:cursor-not-allowed disabled:opacity-40"
                >
                  <Icon size="xs"><CloseIcon /></Icon>
                </button>
              </div>
            )}

            {/* Empty state */}
            {cropTypes.length === 0 && !showAdd && (
              <EmptyState
                emoji="🌾"
                title={t('cropTypes.empty')}
                description={t('cropTypes.emptyHelp')}
                action={
                  <Button
                    variant="primary"
                    size="sm"
                    icon={<Icon size="sm"><PlusIcon /></Icon>}
                    onClick={handleAddOpen}
                  >
                    {t('cropTypes.add')}
                  </Button>
                }
              />
            )}

            {/* Crop type rows */}
            {cropTypes.map(ct => {
              const inUse = usedIds.has(ct.id);
              const isEditing = editingId === ct.id;

              if (isEditing) {
                return (
                  <div key={ct.id} className="flex items-start gap-2 border-b border-green-100 px-4 py-3 last:border-b-0">
                    <div className="flex-1">
                      <input
                        ref={editInputRef}
                        type="text"
                        value={editName}
                        onChange={e => {
                          setEditName(e.target.value);
                          if (editError) setEditError(null);
                        }}
                        onBlur={handleEditBlur}
                        onKeyDown={e => {
                          if (e.key === 'Enter') handleEditSave();
                          if (e.key === 'Escape') handleEditCancel();
                        }}
                        maxLength={80}
                        disabled={editSaving}
                        className="w-full rounded-sm border border-gray-300 px-3 py-1.5 font-body text-sm text-gray-900 outline-none transition-agrobook duration-agrobook focus:border-green-600 focus:ring-4 focus:ring-green-600/15 disabled:bg-gray-50 disabled:text-gray-400"
                      />
                      {editError && (
                        <p className="mt-1 font-body text-xs text-red-600">{editError}</p>
                      )}
                    </div>
                    <Button variant="primary" size="sm" onClick={handleEditSave} disabled={editSaving}>
                      {editSaving ? <Spinner size="sm" /> : t('cropTypes.save')}
                    </Button>
                    <button
                      type="button"
                      onClick={handleEditCancel}
                      disabled={editSaving}
                      aria-label={t('cropTypes.cancelEdit')}
                      className="flex h-8 w-8 items-center justify-center rounded-sm text-gray-400 transition-agrobook duration-agrobook hover:text-gray-700 disabled:cursor-not-allowed disabled:opacity-40"
                    >
                      <Icon size="xs"><CloseIcon /></Icon>
                    </button>
                  </div>
                );
              }

              return (
                <div key={ct.id} className="flex items-center gap-2 border-b border-green-100 px-4 py-3 last:border-b-0">
                  <span className="flex-1 font-body text-sm font-medium text-gray-900">
                    {ct.name}
                  </span>
                  {inUse && (
                    <span className="me-1 font-body text-xs text-gray-400">
                      {t('cropTypes.inUse')}
                    </span>
                  )}
                  <button
                    type="button"
                    aria-label={`${t('cropTypes.editLabel')} ${ct.name}`}
                    onClick={() => handleEditStart(ct)}
                    className="flex h-8 w-8 items-center justify-center rounded-sm border border-gray-300 bg-white text-gray-500 transition-agrobook duration-agrobook hover:border-green-400 hover:bg-green-100 hover:text-green-800"
                  >
                    <Icon size="xs"><PencilIcon /></Icon>
                  </button>
                  <button
                    type="button"
                    aria-label={`${t('cropTypes.deleteLabel')} ${ct.name}`}
                    disabled={inUse}
                    title={inUse ? t('cropTypes.deleteDisabledTooltip') : undefined}
                    onClick={() => !inUse && handleDeleteClick(ct)}
                    className="flex h-8 w-8 items-center justify-center rounded-sm border border-gray-300 bg-white text-gray-500 transition-agrobook duration-agrobook hover:border-red-200 hover:bg-red-50 hover:text-red-600 disabled:cursor-not-allowed disabled:opacity-40"
                  >
                    <Icon size="xs"><TrashIcon /></Icon>
                  </button>
                </div>
              );
            })}
          </Card>
        )}
      </main>

      <FAB ariaLabel={t('cropTypes.add')} onClick={handleAddOpen} />

      <ConfirmDialog
        isOpen={!!deleteTarget}
        title={t('cropTypes.deleteTitle')}
        message={
          <>
            {t('cropTypes.deleteMessagePre')}{' '}
            <strong>{deleteTarget?.name}</strong>
            {t('cropTypes.deleteMessagePost')}
          </>
        }
        confirmLabel={t('cropTypes.deleteConfirm')}
        loadingLabel={t('cropTypes.deleting')}
        loading={deleting}
        onConfirm={handleDeleteConfirm}
        onCancel={() => setDeleteTarget(null)}
      />
    </div>
  );
}
